import React, { useState } from 'react';
import { NextPage } from 'next';
import { AnnualUsageUnit, CustomerType } from 'services/orders/types';
import { Commodity, Utility } from 'services/delivery/types';
import { getNearRetailLocations, getRetailLocation, getRetailLocationByZipCode } from 'evcharging/services/cmsClient';
import { RetailLocation } from 'evcharging/services/types';
import services from 'services';
import { BROKER_ID } from 'config/services';
import { DEFAULT_ELECTRICITY_ANNUAL_USAGE } from 'config/retail';
import { GetBestPlansRequest, GetPlansRequest, GetPlansResponse, Plan } from 'services/quotes/types';
import { ALL_PLANS_TERMS, PLANS_DEFAULT_SORT, PLANS_PAGE_SIZE, PLANS_PREFERRED_TERMS } from 'config/quotes';
import { calcMinEffectiveDate, effectiveDateToString } from 'utils/retail/plans';
import useEnergyRates, { EnergyRatesQuery } from 'hooks/useEnergyRates';
import EvcAdapter from 'components/adapters/EvcAdapter';
import { useRouter } from 'next/router';
import { ENERGY_RATES_PLAN_ROUTE, ENERGY_RATES_ROUTE } from 'config/retail/routes';
import { dateToHumanFormat } from 'utils/common/date';
import { ENV, ENVIRONMENT, setENV } from 'evcharging/app';
import RatesBreadcrumbs from 'containers/retail/sections/energy-rates-3/RatesBreadcrumbs';
import RatesHeader from 'containers/retail/sections/energy-rates-3/RatesHeader';
import Spacer from 'evcharging/components/Spacer';
import RatesFilter from 'containers/retail/sections/energy-rates-3/RatesFilter';
import RatesBestPlans from 'containers/retail/sections/energy-rates-3/RatesBestPlans';
import RatesCustomQuoteModal from 'containers/retail/sections/energy-rates-3/RatesCustomQuoteModal';
import RatesNoDataBox from 'containers/retail/sections/energy-rates-3/RatesNoDataBox';
import StayUpdated from 'evcharging/containers/layout/StayUpdated';
import RatesAllPlans from 'containers/retail/sections/energy-rates-3/RatesAllPlans';
import NewNav from 'containers/common/sections/layout/NewNav/NewNav';
import Container from 'evcharging/components/Container';
import Footer from 'evcharging/containers/layout/Footer';
import RatesWrapper from 'containers/retail/sections/energy-rates-3/RatesWrapper';
import { getDictionary } from 'i18n';
import { I18nPage } from 'i18n/types/pages.enum';
import useI18n from 'evcharging/hooks/useI18n';
import Metadata from 'evcharging/containers/layout/Metadata';

interface EnergyRatesProps {
  retailLocation: RetailLocation;
  nearLocations: RetailLocation[];
  plansResponse: GetPlansResponse;
  bestPlans: Plan[];
  utilities: Utility[];
  selectedUtilityId: string;
  env: ENVIRONMENT;
}

const EnergyRates: NextPage<EnergyRatesProps> = (props) => {
  const { t } = useI18n();

  const [data, setData] = useState<Partial<EnergyRatesProps>>(props);
  const {
    retailLocation,
    nearLocations = [],
    plansResponse,
    bestPlans = [],
    utilities = [],
    selectedUtilityId = "",
    env
  } = data;

  setENV(env);

  const hasLocation = !!retailLocation;
  const hasNearLocations = !!nearLocations.length;
  const hasDescription = !!retailLocation && !!retailLocation.description;
  const hasLocationAndUtilities = !!retailLocation && utilities.length > 0;

  const router = useRouter();

  const [showModal, setShowModal] = useState<boolean>(false);
  const toggleModal = () => setShowModal(!showModal);

  const {
    isLoadingAllPlans,
    isLoadingBestPlans,
    page,
    sort,
    effectiveDate,
    refresh,
    minEffectiveDate,
    term,
    utilityId,
    customerType,
  } = useEnergyRates(async (query) => {
    const result = await fetchData(query, retailLocation);
    setData({ ...data, ...result });
  });

  const filter = { effectiveDate, minEffectiveDate, term };

  const onChangeZipCode = async (value: string) => {
    const loc = await getRetailLocationByZipCode(value);
    window.location.href = ENERGY_RATES_ROUTE({ slug: loc ? loc.slug : value });
  };

  const onChangeUtilityId = (value: string) => {
    return refresh({ utilityId: value, page: 1 });
  };

  const onChangeCustomerType = (value: CustomerType) => {
    return refresh({ customerType: value, page: 1 });
  };

  const onPage = (value: number) => {
    return refresh({ page: value }, false);
  };

  const onSort = (value: string) => {
    return refresh({ sort: value, page: 1 }, false);
  };

  const onFilter = (field: string, value: any) => {
    return refresh({ [field]: value, page: 1 }, field !== "term");
  };

  const onSelectPlan = (plan: Plan) => {
    return router.push(ENERGY_RATES_PLAN_ROUTE(plan.planId, { utilityId, retailLocationSlug: retailLocation.slug }));
  };

  const locationText = hasLocation
    ? `${retailLocation.city}, ${retailLocation.state}`
    : t('rates.your-location');

  return (
    <>
      <Metadata
        title={retailLocation?.meta_title}
        description={retailLocation?.meta_description}
      />
      <EvcAdapter>
        <NewNav />
        <RatesCustomQuoteModal
          show={showModal}
          onClose={() => setShowModal(false)}
          customerType={customerType}
          zipCode={retailLocation.zip_code}
          utilityName={utilities.find((utility) => utility.utilityId === utilityId || utility.utilityId === selectedUtilityId)?.name || ''}
        />
        <RatesWrapper>
          <Container>
            <RatesBreadcrumbs />

            <RatesHeader
              title={t('rates.in-location', { location: locationText })}
              subTitle={t('rates.updated-at', { date: dateToHumanFormat(new Date()) })}
            />
            <Spacer value={24} />

            <RatesFilter
              utilities={utilities}
              zipCode={retailLocation?.zip_code || ""}
              utilityId={utilityId || selectedUtilityId}
              customerType={customerType as CustomerType}
              onChangeZipCode={onChangeZipCode}
              onChangeUtilityId={onChangeUtilityId}
              onChangeCustomerType={onChangeCustomerType}
            />

            {hasLocationAndUtilities || (
              <>
                <Spacer value={16} />
                <RatesNoDataBox
                  retailLocation={retailLocation}
                  customerType={customerType}
                  zipCode={retailLocation.zip_code}
                  utilityName={utilities.find((utility) => utility.utilityId === utilityId || utility.utilityId === selectedUtilityId)?.name || ''}
                />
              </>
            )}
            {hasLocationAndUtilities && (
              <>
                <Spacer value={16} />

                <RatesBestPlans
                  toggleModal={toggleModal}
                  locationText={locationText}
                  bestPlans={bestPlans}
                  onSelectPlan={onSelectPlan}
                  isLoading={isLoadingBestPlans}
                />

                <Spacer value={32} />

                <RatesAllPlans
                  locationText={locationText}
                  plansResponse={plansResponse}
                  sort={sort}
                  filter={filter}
                  page={page}
                  isLoading={isLoadingAllPlans}
                  onPage={onPage}
                  onSort={onSort}
                  onFilter={onFilter}
                  onSelectPlan={onSelectPlan}
                />
              </>
            )}
            {/*{hasDescription && (
              <>
                <Spacer value={64} />
                <PostText>{retailLocation.description}</PostText>
              </>
            )}

            {hasNearLocations && (
              <>
                <Spacer value={64} />
                <RatesNearLocations nearLocations={nearLocations} />
                <Spacer value={64} />
              </>
            )}*/}
          </Container>
        </RatesWrapper>
        <StayUpdated />
        <Footer />
      </EvcAdapter>
    </>
  );
};

const getBestPlans = async ({
    state,
    effectiveDate,
    utilityId,
    classificationId,
    zipCode,
    customerType,
    zoneId,
  }: Partial<GetBestPlansRequest>) => {
  const bestPlans = await services.quotesService.getBestPlans({
    brokerId: BROKER_ID,
    customerType: customerType,
    commodity: Commodity.ELECTRICITY,
    annualUsage: DEFAULT_ELECTRICITY_ANNUAL_USAGE[customerType],
    unit: AnnualUsageUnit.KWH,
    state,
    preferredTerms: PLANS_PREFERRED_TERMS,
    effectiveDate,
    utilityId,
    classificationId,
    zipCode,
    zoneId,
  });

  return bestPlans.bestPlans;
};

const getAllPlans = ({
     state,
     term,
     effectiveDate,
     utilityId, customerType,
     classificationId,
     zipCode,
     page,
     sort,
     zoneId,
   }: Partial<GetPlansRequest>) => {
  return services.quotesService.getPlans({
    brokerId: BROKER_ID,
    customerType: customerType,
    commodity: Commodity.ELECTRICITY,
    annualUsage: DEFAULT_ELECTRICITY_ANNUAL_USAGE[customerType],
    unit: AnnualUsageUnit.KWH,
    size: PLANS_PAGE_SIZE,
    state,
    term,
    effectiveDate,
    utilityId,
    classificationId,
    zipCode,
    page,
    sort,
    zoneId,
  });
};

export const getUtilities = async (zipCode: string) => {
  const utilities = await services.deliveryService.getUtilityByZipCode(zipCode);

  return utilities.filter((utility) =>
    utility.commodities.some((item) => item.commodity === Commodity.ELECTRICITY)
  );
};

const fetchData = async (
  query: EnergyRatesQuery,
  retailLocation: RetailLocation
): Promise<Partial<EnergyRatesProps>> => {
  const page = query.page ? Number(query.page) : 1;
  const sort = query.sort ? String(query.sort) : PLANS_DEFAULT_SORT;
  const minEffectiveDate = calcMinEffectiveDate();
  const effectiveDate = query.effectiveDate
    ? String(query.effectiveDate)
    : effectiveDateToString(minEffectiveDate);
  const preferredTerms = query.term ? String(query.term) : PLANS_PREFERRED_TERMS;
  const allTerms = query.term ? String(query.term) : ALL_PLANS_TERMS;
  const utilityId = query.utilityId ? String(query.utilityId) : "";
  const customerType = query.customerType ? String(query.customerType) as CustomerType : CustomerType.RESIDENTIAL;

  const utilities = await getUtilities(retailLocation.zip_code);
  const selectedUtility =
    utilities.find((utility) => utility.utilityId === utilityId) || utilities[0];
  const selectedUtilityId = selectedUtility?.utilityId;
  const classification = selectedUtility?.commodities[0]?.classifications ?
    (selectedUtility?.commodities[0]?.classifications.find((cl) => cl.customerTypes.includes(customerType)) ||
      selectedUtility?.commodities[0]?.classifications[0]) : null;

  const zoneId = selectedUtility?.commodities[0]?.zones && selectedUtility?.commodities[0]?.zones.length ? selectedUtility?.commodities[0]?.zones[0].zoneId : undefined;

  const params = {
    state: retailLocation.st,
    preferredTerms,
    effectiveDate,
    utilityId: selectedUtilityId,
    customerType,
    classificationId: classification?.classificationId,
    zipCode: retailLocation.zip_code,
    zoneId,
  };

  const bestPlans = await getBestPlans(params);
  const plansResponse = await getAllPlans({ ...params, page, sort, term: allTerms });

  return {
    utilities,
    selectedUtilityId,
    bestPlans,
    plansResponse
  };
};

export async function getStaticProps({ params: urlParams, locale, defaultLocale }) {
  const { slug } = urlParams;

  const i18n = await getDictionary(locale, defaultLocale, [I18nPage.RetailEnergyRates, I18nPage.EnergyRates]);

  const getFullResult = (props: Partial<EnergyRatesProps>) => {
    return { props: { ...props, page: {}, i18n, env: ENV }, revalidate: 3600 };
  };

  const page = 1;
  const sort = PLANS_DEFAULT_SORT;
  const minEffectiveDate = calcMinEffectiveDate();
  const effectiveDate = effectiveDateToString(minEffectiveDate);
  const preferredTerms = PLANS_PREFERRED_TERMS;
  const allTerms = ALL_PLANS_TERMS;
  const utilityId = "";
  const customerType = CustomerType.RESIDENTIAL;
  const retailLocation = await getRetailLocation(slug);

  if (!retailLocation) {
    return getFullResult({ retailLocation });
  }

  const nearLocations = await getNearRetailLocations(retailLocation.zip_code);

  try {
    const utilities = await getUtilities(retailLocation.zip_code);

    const selectedUtility =
      utilities.find((utility) => utility.utilityId === utilityId) || utilities[0];
    const selectedUtilityId = selectedUtility?.utilityId;

    const classification = selectedUtility?.commodities[0]?.classifications ?
      (selectedUtility?.commodities[0]?.classifications.find((cl) => cl.customerTypes.includes(customerType)) ||
      selectedUtility?.commodities[0]?.classifications[0]) : null;
    const zoneId = selectedUtility?.commodities[0]?.zones && selectedUtility?.commodities[0]?.zones.length ? selectedUtility?.commodities[0]?.zones[0].zoneId : undefined;

    const params = {
      state: retailLocation.st,
      preferredTerms,
      effectiveDate,
      utilityId: selectedUtilityId,
      customerType,
      classificationId: classification?.classificationId,
      zipCode: retailLocation.zip_code,
      zoneId,
    };

    const bestPlans = await getBestPlans(params);
    const plansResponse = await getAllPlans({ ...params, page, sort, term: allTerms });

    return getFullResult({
      retailLocation,
      utilities,
      selectedUtilityId,
      bestPlans,
      plansResponse,
      nearLocations
    });
  } catch (e) {
    console.error(e);
    return getFullResult({ retailLocation, nearLocations });
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export default EnergyRates;
